import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppState } from '../context/AppStateContext';
import { useApi } from '../hooks/useApi';
import { useQuery } from '@tanstack/react-query';
import { calculate8Level } from '../utils/cbcUtils';
import CompareControls from './dashboard/CompareControls';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';

import TeacherInfo from './TeacherInfo';
import FloatingManualButton from './common/FloatingManualButton';

import {
    ClipboardDocumentCheckIcon,
    UserGroupIcon,
    BookOpenIcon,
    DocumentTextIcon,
    FireIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';

const StatCard = ({ label, value, icon: Icon, badge, onClick }) => (
    <div
        onClick={onClick}
        className={`bg-white rounded-2xl md:rounded-3xl shadow-sm border border-slate-100 p-3 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-5 hover:shadow-md transition-all h-full ${onClick ? 'cursor-pointer hover:scale-[1.02] hover:border-indigo-100' : ''}`}
    >
        <div className="h-10 w-10 md:h-14 md:w-14 rounded-xl md:rounded-2xl bg-[#18216D]/5 text-[#18216D] flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5 md:h-7 md:w-7" />
        </div>
        <div className="min-w-0 flex-1 w-full">
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-black truncate">{label}</p>
            <p className="text-lg md:text-3xl font-black text-[#18216D] leading-none mt-1 truncate">{value}</p>
            {badge && <p className="text-[9px] md:text-[10px] font-bold text-[#FFC425] bg-[#FFC425]/10 px-2 py-0.5 rounded-lg mt-1 md:mt-2 inline-block uppercase tracking-tighter truncate max-w-full">{badge}</p>}
        </div>
    </div>
);

const SubmissionDetailModal = ({ courses, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Flatten assignments from all courses
    const allAssignments = useMemo(() => {
        return courses.flatMap(course =>
            (course.assignments || []).map(asgn => ({
                ...asgn,
                courseName: course.name,
                courseCode: course.code,
                // Calculated fields for sorting
                ungradedCount: (asgn.submission_count || 0) - (asgn.graded_submissions_count || 0)
            }))
        ).sort((a, b) => {
            // Sort by ungraded count descending (highest priority)
            if (b.ungradedCount !== a.ungradedCount) {
                return b.ungradedCount - a.ungradedCount;
            }
            // Fallback to due date (most recent first)
            return new Date(b.due_date) - new Date(a.due_date);
        });
    }, [courses]);

    const filteredAssignments = useMemo(() => {
        if (!searchTerm) return allAssignments;
        const lowerTerm = searchTerm.toLowerCase();
        return allAssignments.filter(a =>
            a.title.toLowerCase().includes(lowerTerm) ||
            a.courseName.toLowerCase().includes(lowerTerm) ||
            a.courseCode.toLowerCase().includes(lowerTerm)
        );
    }, [allAssignments, searchTerm]);

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
                <div className="p-8 border-b border-slate-100 flex items-center justify-between gap-6">
                    <div>
                        <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tight">Submission Overview</h2>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Global Assignment Tracking</p>
                    </div>

                    {/* Search Input */}
                    <div className="flex-1 max-w-md relative group">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors">
                            <i className="fas fa-search"></i>
                        </div>
                        <input
                            type="text"
                            placeholder="Search tasks or learning areas..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all text-sm font-bold text-[#18216D]"
                        />
                    </div>

                    <button onClick={onClose} className="h-10 w-10 rounded-full bg-slate-50 text-slate-400 hover:bg-rose-50 hover:text-rose-500 flex items-center justify-center transition-all">
                        <span className="sr-only">Close</span>
                        <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                    </button>
                </div>

                <div className="overflow-y-auto p-8 space-y-4 custom-scrollbar">
                    {filteredAssignments.length > 0 ? (
                        filteredAssignments.map(assignment => {
                            const total = assignment.submission_count || 0;
                            const graded = assignment.graded_submissions_count || 0;
                            const percentage = total > 0 ? Math.round((graded / total) * 100) : 0;

                            return (
                                <div key={assignment.id} className="p-5 rounded-2xl border border-slate-100 hover:border-indigo-100 hover:bg-slate-50/50 transition-all group">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <span className="px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-[10px] font-black uppercase tracking-widest border border-indigo-100">
                                                {assignment.courseCode}
                                            </span>
                                            <h3 className="font-black text-[#18216D]">{assignment.title}</h3>
                                        </div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                            Due {new Date(assignment.due_date).toLocaleDateString()}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-6">
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between text-xs mb-1.5">
                                                <span className="font-bold text-slate-500 uppercase tracking-wider">Grading Progress</span>
                                                <span className="font-black text-[#18216D]">{graded} / {total} Submissions</span>
                                            </div>
                                            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full transition-all duration-500 ${percentage === 100 ? 'bg-emerald-500' : 'bg-[#FFC425]'}`}
                                                    style={{ width: `${percentage}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                        <div className="text-right min-w-[80px]">
                                            <p className="text-2xl font-black text-[#18216D]">{total}</p>
                                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Received</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center py-20">
                            <DocumentTextIcon className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                            <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No assignments found</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const TeacherDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { courses, uniqueStudentCount, pendingActions, loading, error, refresh } = useAppState();
    const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const cardsPerPage = 3;
    const { get } = useApi();

    const [selectedTerm, setSelectedTerm] = useState('');
    const [comparisonFilters, setComparisonFilters] = useState({});

    // Fetch terms
    const { data: termsData } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
        staleTime: 60 * 60 * 1000,
    });
    const terms = useMemo(() => termsData || [], [termsData]);

    // Set default term
    useEffect(() => {
        if (terms.length > 0 && !selectedTerm) {
            const currentTerm = terms.find(t => t.is_current);
            setSelectedTerm(currentTerm ? currentTerm.id.toString() : terms[0].id.toString());
        }
    }, [terms, selectedTerm]);

    // Fetch Target Performance Matrix
    const { data: chartTargetData, isLoading: chartTargetLoading } = useQuery({
        queryKey: ['teacher-performance-matrix-target', user?.id, selectedTerm, comparisonFilters.target_term_id, comparisonFilters.target_year_id],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (comparisonFilters.target_term_id) params.append('term_id', comparisonFilters.target_term_id);
            else if (comparisonFilters.target_year_id) params.append('academic_year', comparisonFilters.target_year_id);
            else if (selectedTerm) params.append('term_id', selectedTerm);
            return get(`/api/teacher/performance-matrix/?${params.toString()}`);
        },
        enabled: !!user && (!!selectedTerm || !!comparisonFilters.target_term_id || !!comparisonFilters.target_year_id),
    });

    // Fetch Comparison Performance Matrix
    const { data: chartCompareData, isLoading: chartCompareLoading } = useQuery({
        queryKey: ['teacher-performance-matrix-compare', user?.id, comparisonFilters.compare_term_id, comparisonFilters.compare_year_id],
        queryFn: async () => {
            if (!comparisonFilters.compare_term_id && !comparisonFilters.compare_year_id) return null;
            const params = new URLSearchParams();
            if (comparisonFilters.compare_term_id) params.append('term_id', comparisonFilters.compare_term_id);
            else if (comparisonFilters.compare_year_id) params.append('academic_year', comparisonFilters.compare_year_id);
            return get(`/api/teacher/performance-matrix/?${params.toString()}`);
        },
        enabled: !!user && (!!comparisonFilters.compare_term_id || !!comparisonFilters.compare_year_id),
    });

    // Fetch upcoming lesson plans (both scheduled and templates)
    const { data: lessonPlansData, isLoading: lessonPlansLoading } = useQuery({
        queryKey: ['teacher-lesson-plans', user?.id],
        queryFn: () => get('/api/cbc/lesson-plans/'),
        enabled: !!user,
    });

    const isComparisonEnabled = !!(comparisonFilters.compare_term_id || comparisonFilters.compare_year_id);

    // Compute Chart Data
    const chartData = useMemo(() => {
        const subjectMap = new Map();
        [chartTargetData, chartCompareData].forEach(dataset => {
            if (dataset?.subjects) {
                dataset.subjects.forEach(sub => {
                    if (!subjectMap.has(sub.code)) subjectMap.set(sub.code, { code: sub.code, name: sub.name });
                });
            }
        });

        if (subjectMap.size === 0) return [];
        
        return Array.from(subjectMap.values()).map(sub => {
            const targetScores = (chartTargetData?.students || [])
                .map(s => s.subject_scores?.[sub.code])
                .filter(sc => sc && sc.percentage !== null)
                .map(sc => sc.percentage);
            const targetAvg = targetScores.length > 0 ? targetScores.reduce((a, b) => a + b, 0) / targetScores.length : 0;

            const compareScores = (chartCompareData?.students || [])
                .map(s => s.subject_scores?.[sub.code])
                .filter(sc => sc && sc.percentage !== null)
                .map(sc => sc.percentage);
            const compareAvg = compareScores.length > 0 ? compareScores.reduce((a, b) => a + b, 0) / compareScores.length : 0;

            return {
                learning_area: sub.name || sub.code,
                class_average: Math.round(targetAvg),
                compare_average: Math.round(compareAvg),
                level: targetAvg > 0 ? calculate8Level(targetAvg) : 'N/A',
                compare_level: compareAvg > 0 ? calculate8Level(compareAvg) : 'N/A'
            };
        });
    }, [chartTargetData, chartCompareData]);


    const overview = useMemo(() => {
        const totalStudents = uniqueStudentCount || 0;
        const totalAssignments = courses.reduce((acc, course) => acc + (course.assignments?.length || 0), 0);

        // Calculate total submissions across all assignments
        const totalSubmissions = courses.reduce((acc, course) => {
            return acc + (course.assignments?.reduce((a, asgn) => a + (asgn.submission_count || 0), 0) || 0);
        }, 0);

        const gradedCount = courses.reduce((acc, course) => {
            return acc + (course.assignments?.reduce((a, asgn) => a + (asgn.graded_submissions_count || 0), 0) || 0);
        }, 0);

        return {
            learningAreas: courses.length,
            students: totalStudents,
            assignments: totalAssignments,
            submissions: totalSubmissions,
            gradedAssignments: gradedCount
        };
    }, [courses, uniqueStudentCount]);

    const upcomingLessonPlans = useMemo(() => {
        if (!lessonPlansData) return [];
        const plans = Array.isArray(lessonPlansData) ? lessonPlansData : (lessonPlansData.results || []);
        
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const sevenDaysFromNow = new Date(startOfToday.getTime() + 8 * 24 * 60 * 60 * 1000 - 1);

        const upcomingPlans = plans
            .filter(plan => {
                if (!plan.lesson_date) return false;
                const [year, month, day] = plan.lesson_date.split('T')[0].split('-').map(Number);
                const lessonDate = new Date(year, month - 1, day);
                return lessonDate >= startOfToday && lessonDate <= sevenDaysFromNow;
            })
            .sort((a, b) => new Date(a.lesson_date) - new Date(b.lesson_date))
            .slice(0, 5);

        return upcomingPlans;
    }, [lessonPlansData]);

    const handleRefresh = () => {
        refresh();
    };

    if (loading) return <div className="text-center py-8">Loading...</div>;
    if (error)
        return (
            <div className="text-red-500 p-4">
                {error}
                <button onClick={handleRefresh} className="ml-4 text-blue-600 hover:underline">
                    Retry
                </button>
            </div>
        );

    return (
        <div className="min-h-screen bg-slate-50">
            <main className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8 space-y-10">
                <header className="relative overflow-hidden bg-[#18216D] rounded-[2.5rem] p-12 text-white shadow-2xl shadow-indigo-900/20">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC425]/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                    <div className="relative z-10">
                        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#FFC425] mb-2">Teachers Dashboard</p>
                        <h1 className="text-4xl sm:text-6xl font-black tracking-tighter">Welcome back, {user?.first_name || 'Teacher'}</h1>
                        <div className="h-2 w-24 bg-[#FFC425] rounded-full mt-6"></div>
                    </div>
                </header>

                <section className="grid grid-cols-3 gap-2 md:gap-4 pb-4 md:pb-0">
                    <div className="col-span-1">
                        <StatCard label="Learning area" value={overview.learningAreas} icon={BookOpenIcon} onClick={() => navigate('/teacher/courses')} />
                    </div>
                    <div className="col-span-1">
                        <StatCard label="Students" value={overview.students} icon={UserGroupIcon} />
                    </div>
                    <div className="col-span-1">
                        <StatCard
                            label="Submissions"
                            value={overview.submissions}
                            icon={ClipboardDocumentCheckIcon}
                            badge={`${overview.gradedAssignments} Graded`}
                            onClick={() => navigate('/teacher/submissions')}
                        />
                    </div>
                </section>

                <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="hidden lg:block lg:col-span-1">
                        <TeacherInfo />
                    </div>
                    <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100">
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">National Standards</p>
                                <h2 className="text-xl font-semibold text-gray-900">Assigned Learning Areas</h2>
                            </div>
                            <div className="relative w-64 lg:w-80">
                                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                                    <i className="fas fa-search"></i>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search learning areas..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all text-sm font-medium text-[#18216D]"
                                />
                            </div>
                        </div>
                        {courses.length === 0 ? (
                            <div className="p-6 text-gray-500 text-sm italic">
                                No national standards assigned yet. Please contact administration to link your profile to your Learning Areas.
                            </div>
                        ) : (() => {
                            // Filter courses based on search term
                            const filteredCourses = courses.filter(course =>
                                course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                course.code.toLowerCase().includes(searchTerm.toLowerCase())
                            );

                            const indexOfLastCard = currentPage * cardsPerPage;
                            const indexOfFirstCard = indexOfLastCard - cardsPerPage;
                            const currentCourses = filteredCourses.slice(indexOfFirstCard, indexOfLastCard);
                            const totalPages = Math.ceil(filteredCourses.length / cardsPerPage);

                            return (
                                <>
                                    <div className="p-6 space-y-4">
                                        {currentCourses.map((course) => (
                                            <div
                                                key={course.id}
                                                onClick={() => navigate(`/teacher/courses/${course.id}`)}
                                                className="block border border-slate-100 rounded-[2rem] p-6 hover:border-[#FFC425] hover:bg-white hover:shadow-xl hover:shadow-indigo-900/5 transition-all cursor-pointer group"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FFC425]">
                                                            {course.grade_level_name} ({course.stream_display_name})
                                                        </p>
                                                        <h3 className="text-xl font-black text-[#18216D] group-hover:translate-x-1 transition-transform">{course.name}</h3>
                                                    </div>
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 px-3 py-1 rounded-full border border-slate-100">
                                                        {course.enrolled_students_count || 0} Scholars
                                                    </span>
                                                </div>
                                                <div className="mt-4 grid gap-3">
                                                    {(() => {
                                                        const now = new Date();
                                                        return [...(course.assignments || [])]
                                                            .sort((a, b) => {
                                                                const aDue = new Date(a.due_date);
                                                                const bDue = new Date(b.due_date);
                                                                const aOverdue = aDue < now;
                                                                const bOverdue = bDue < now;

                                                                // Non-overdue first
                                                                if (aOverdue !== bOverdue) return aOverdue ? 1 : -1;
                                                                // Then closest due date first
                                                                return aDue - bDue;
                                                            })
                                                            .slice(0, 2)
                                                            .map((assignment) => (
                                                                <div
                                                                    key={assignment.id}
                                                                    className="flex items-center justify-between bg-white bg-opacity-50 rounded-lg px-3 py-2 border border-gray-50"
                                                                >
                                                                    <div>
                                                                        <p className="text-sm font-semibold text-gray-800">
                                                                            {assignment.title}
                                                                        </p>
                                                                        <p className="text-xs text-gray-500">
                                                                            Due {new Date(assignment.due_date).toLocaleDateString()}
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex items-center gap-3">
                                                                        {!assignment.is_manual ? (
                                                                            <Link
                                                                                to={`/teacher/assignments/${assignment.id}/submissions`}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                }}
                                                                                className="p-2 text-slate-300 hover:text-[#18216D] transition-colors"
                                                                                title="View Submissions"
                                                                            >
                                                                                <DocumentTextIcon className="w-5 h-5" />
                                                                            </Link>
                                                                        ) : (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    navigate(`/teacher/courses/${course.id}/grade/${assignment.id}`);
                                                                                }}
                                                                                className="text-[10px] font-black uppercase tracking-widest text-[#18216D] hover:text-[#FFC425] transition-all"
                                                                            >
                                                                                Grade Task
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ));
                                                    })()}
                                                    {(!course.assignments || !course.assignments.length) && (
                                                        <p className="text-sm text-gray-400">
                                                            No assignments configured for this learning area.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {totalPages > 1 && (
                                        <div className="px-6 pb-6 flex items-center justify-center gap-4">
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                disabled={currentPage === 1}
                                                className="px-4 py-2 bg-slate-50 text-[#18216D] rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-100 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                            >
                                                Previous
                                            </button>
                                            <span className="text-xs font-black text-slate-400 uppercase tracking-widest">
                                                Page {currentPage} of {totalPages}
                                            </span>
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                disabled={currentPage === totalPages}
                                                className="px-4 py-2 bg-slate-50 text-[#18216D] rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-100 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </section>

                <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Upcoming Lesson Plans Card */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-black text-[#18216D] flex items-center uppercase tracking-tight">
                                <BookOpenIcon className="w-6 h-6 mr-3 text-[#FFC425]" />
                                Upcoming Lesson Plans
                            </h3>
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                                Next 7 Days
                            </span>
                        </div>
                        <div className="space-y-4 flex-1">
                            {lessonPlansLoading ? (
                                <div className="text-center py-12">
                                    <div className="animate-pulse flex flex-col items-center justify-center">
                                        <div className="h-16 w-16 bg-slate-100 rounded-[1.5rem] mb-4"></div>
                                        <div className="h-4 w-32 bg-slate-100 rounded"></div>
                                    </div>
                                </div>
                            ) : upcomingLessonPlans.length > 0 ? (
                                upcomingLessonPlans.map(plan => {
                                    const hasDate = !!plan.lesson_date;
                                    let lessonDate = null;
                                    let daysUntilDue = null;
                                    if (hasDate) {
                                        const [year, month, day] = plan.lesson_date.split('T')[0].split('-').map(Number);
                                        lessonDate = new Date(year, month - 1, day);
                                        daysUntilDue = Math.round((lessonDate - new Date(new Date().setHours(0,0,0,0))) / (1000 * 60 * 60 * 24));
                                    }
                                    const isUrgent = daysUntilDue !== null && daysUntilDue <= 2;

                                    return (
                                        <div key={plan.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 bg-slate-50/50 rounded-2xl border border-slate-100/50 hover:bg-white hover:shadow-xl hover:shadow-indigo-900/5 transition-all group gap-4">
                                            <div className="flex items-start gap-4 flex-1 min-w-0">
                                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-inner ${isUrgent ? 'bg-[#FFC425]/10 text-[#FFC425]' : 'bg-[#18216D]/5 text-[#18216D]'}`}>
                                                    <BookOpenIcon className="w-6 h-6 stroke-[2]" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                                        <span className="px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-[9px] font-black uppercase tracking-widest border border-indigo-100/50">
                                                            {plan.learning_area_name}
                                                        </span>
                                                        {plan.strand_name && (
                                                            <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600 text-[9px] font-bold uppercase tracking-widest border border-slate-200/50">
                                                                {plan.strand_name}
                                                            </span>
                                                        )}
                                                        {isUrgent && (
                                                            <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[8px] font-black uppercase tracking-widest animate-pulse">
                                                                Soon
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="font-black text-base text-[#18216D] tracking-tight leading-tight mb-1">{plan.title}</p>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                        {hasDate
                                                            ? `${lessonDate.toLocaleDateString()} • ${daysUntilDue === 0 ? 'Today' : daysUntilDue === 1 ? 'Tomorrow' : `In ${daysUntilDue} days`}`
                                                            : `${plan.grade_level_name || ''} ${plan.stream_name ? `• ${plan.stream_name}` : ''}`
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => navigate(`/teacher/lesson-plan/editor/${plan.id}`)}
                                                className="w-full sm:w-auto px-5 py-2.5 bg-[#18216D] text-white text-[9px] font-black rounded-xl hover:bg-[#0D164F] transition-all uppercase tracking-widest shadow-lg shadow-indigo-900/10 text-center shrink-0"
                                            >
                                                View Design
                                            </button>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-center py-12 flex flex-col items-center justify-center h-full">
                                    <div className="h-16 w-16 bg-slate-50 rounded-[1.5rem] flex items-center justify-center mb-4 border border-slate-100">
                                        <BookOpenIcon className="w-8 h-8 text-slate-200 stroke-[1.5]" />
                                    </div>
                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">No upcoming lesson plans in the next 7 days</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Pending Actions Card */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-black text-[#18216D] flex items-center uppercase tracking-tight">
                                <i className="fas fa-tasks mr-3 text-[#FFC425]"></i>
                                Pending Action
                            </h3>
                            <Link to="/teacher/courses" className="text-[10px] font-black text-[#18216D] uppercase tracking-[0.2em] hover:text-[#FFC425] transition-colors">
                                View Registry
                            </Link>
                        </div>
                        <div className="space-y-4">
                            {pendingActions && pendingActions.length > 0 ? (
                                pendingActions.map(action => (
                                    <div key={action.id} className="flex items-center justify-between p-4 bg-slate-50/50 rounded-2xl border border-slate-100/50 hover:bg-white hover:shadow-xl hover:shadow-indigo-900/5 transition-all group">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${action.type === 'quiz_result' ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-600'}`}>
                                                {action.type === 'quiz_result' ? <FireIcon className="w-5 h-5" /> : <DocumentTextIcon className="w-5 h-5" />}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="font-black text-sm text-[#18216D] tracking-tight">{action.student_name}</p>
                                                    <span className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest ${action.type === 'quiz_result' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>
                                                        {action.type === 'quiz_result' ? 'Quiz Result' : 'Assignment'}
                                                    </span>
                                                </div>
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                                                    {action.title} • {action.type === 'quiz_result' ? `${action.score}/${action.total}` : new Date(action.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                            </div>
                                        </div>
                                        {action.type === 'assignment' ? (
                                            <button
                                                onClick={() => {
                                                    if (action.is_manual) {
                                                        navigate(`/teacher/courses/${action.learning_area_id}/grade/${action.assignment_id}`);
                                                    } else {
                                                        // Use ActiveMarkingPage routing for digital assignments
                                                        navigate(`/teacher/assignments/${action.assignment_id}/mark/${action.submission_id}`);
                                                    }
                                                }}
                                                className="px-5 py-2.5 bg-[#18216D] text-white text-[9px] font-black rounded-xl hover:bg-[#0D164F] transition-all uppercase tracking-widest shadow-lg shadow-indigo-900/10 text-center"
                                            >
                                                {action.is_manual ? 'Grade Task' : 'Mark'}
                                            </button>
                                        ) : (
                                            <div className="flex items-center gap-2 text-emerald-500">
                                                <CheckCircleIcon className="w-5 h-5" />
                                                <span className="text-[9px] font-black uppercase tracking-widest">Auto-Graded</span>
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-12">
                                    <div className="h-16 w-16 bg-slate-50 rounded-[1.5rem] flex items-center justify-center mx-auto mb-4 border border-slate-100">
                                        <CheckCircleIcon className="w-8 h-8 text-slate-200" />
                                    </div>
                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">The Hub is Clear. No pending actions.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Performance Analytics Chart Section */}
                <section className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-10">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-10">
                        <div>
                            <h3 className="text-2xl font-black text-[#18216D] uppercase tracking-tight italic flex items-center">
                                <i className="fas fa-chart-line mr-3 text-[#FFC425]"></i>
                                Learning Area Averages
                            </h3>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1">
                                {isComparisonEnabled 
                                    ? "Comparative subject performance analysis" 
                                    : `Subject-wide mean score — ${terms.find(t => t.id.toString() === (comparisonFilters.target_term_id || selectedTerm))?.name || 'Current Term'}`
                                }
                            </p>
                        </div>
                        <CompareControls onFilterChange={setComparisonFilters} />
                    </div>

                    <div className="flex items-center gap-6 mb-8 px-2">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-[#18216D]"></div>
                            <span className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">
                                {terms.find(t => t.id.toString() === (comparisonFilters.target_term_id || selectedTerm))?.name || 'Target Period'}
                            </span>
                        </div>
                        {isComparisonEnabled && (
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded bg-[#FFC425]"></div>
                                <span className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">
                                    {comparisonFilters.compare_term_id 
                                        ? (terms.find(t => t.id.toString() === comparisonFilters.compare_term_id.toString())?.name || 'Comparison Term')
                                        : 'Comparison Year'
                                    }
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="h-[400px] w-full">
                        {chartTargetLoading || chartCompareLoading ? (
                            <div className="h-full w-full flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18216D]"></div>
                            </div>
                        ) : chartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={chartData}
                                    margin={{ top: 10, right: 20, left: 10, bottom: 90 }}
                                    barGap={8}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis
                                        dataKey="learning_area"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 9, fontWeight: 700 }}
                                        angle={-40}
                                        textAnchor="end"
                                        interval={0}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 10, fontWeight: 700 }}
                                        domain={[0, 100]}
                                        tickFormatter={v => `${v}%`}
                                    />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(24, 33, 109, 0.03)' }}
                                        content={({ active, payload, label }) => {
                                            if (active && payload && payload.length) {
                                                const d = chartData.find(c => c.learning_area === label);
                                                const isComparison = payload.length > 1;
                                                return (
                                                    <div className="bg-white/95 backdrop-blur-md p-4 rounded-3xl shadow-2xl border border-slate-100 min-w-[160px] pointer-events-none">
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3 border-b border-slate-100 pb-2">{label}</p>
                                                        <div className="space-y-3">
                                                            <div className="flex items-center justify-between gap-4">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-2 h-2 rounded-full bg-[#18216D]"></div>
                                                                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                                                                        {terms.find(t => t.id.toString() === (comparisonFilters.target_term_id || selectedTerm))?.name || 'Target Period'}
                                                                    </p>
                                                                </div>
                                                                <div className="text-right">
                                                                    <span className="text-sm font-black text-[#18216D] italic">{d?.level}</span>
                                                                    <span className="text-[9px] font-bold text-slate-300 ml-1.5">{d?.class_average}%</span>
                                                                </div>
                                                            </div>
                                                            {isComparison && (
                                                                <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-50">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="w-2 h-2 rounded-full bg-[#FFC425]"></div>
                                                                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                                                                            {comparisonFilters.compare_term_id 
                                                                                ? (terms.find(t => t.id.toString() === comparisonFilters.compare_term_id.toString())?.name || 'Comparison')
                                                                                : 'Comparison Year'
                                                                            }
                                                                        </p>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <span className="text-sm font-black text-[#18216D] italic">{d?.compare_level}</span>
                                                                        <span className="text-[9px] font-bold text-slate-300 ml-1.5">{d?.compare_average}%</span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Bar
                                        dataKey="class_average"
                                        fill="#18216D"
                                        radius={[6, 6, 0, 0]}
                                        barSize={32}
                                        animationDuration={1500}
                                    />
                                    {isComparisonEnabled && (
                                        <Bar
                                            dataKey="compare_average"
                                            fill="#FFC425"
                                            radius={[6, 6, 0, 0]}
                                            barSize={32}
                                            animationDuration={1500}
                                        />
                                    )}
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full w-full flex flex-col items-center justify-center bg-slate-50/50 rounded-3xl border border-dashed border-slate-200">
                                <i className="fas fa-chart-bar text-slate-200 text-5xl mb-4"></i>
                                <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">No performance data available for this period</p>
                            </div>
                        )}
                    </div>
                </section>




                {showSubmissionsModal && (
                    <SubmissionDetailModal courses={courses} onClose={() => setShowSubmissionsModal(false)} />
                )}

                <FloatingManualButton url="/manuals/Teacher_User_Manual.pdf" />
            </main>
        </div>
    );
};

export default TeacherDashboard;